"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ConsolidatedReportsTable from "../../../components/ConsolidatedReportsTable/ConsolidatedReportsTable";
import ActiveProject from "../../../assets/icons/Region.svg";
import { Select } from "antd";
import {
  getOperatingPerformanceByUnit,
  getNotesToOperatingByUnit,
  getOperatingPersonalPaymentByUnit,
  getOperatingMiscExpensesByUnit,
} from "../../../redux/adminUnitReportSlice/adminUnitReportSlice";
import CustomTab from "../../../components/Common/CustomTab/CustomTab";

// const DEFAULT_FY = "2026-2027";
const getCurrentFY = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth(); // 0 = Jan

  const fyStartYear = month >= 3 ? year : year - 1; // April-March FY

  return `${fyStartYear}-${fyStartYear + 1}`;
};

const DEFAULT_FY = getCurrentFY();

const tabData = [
  { label: "Operating Performance" },
  { label: "Notes to Operating" },
  { label: "Personnel Payments" },
  { label: "Miscellaneous Expenses" },
];

const monthOptions = [
  { label: "April", value: "04" },
  { label: "May", value: "05" },
  { label: "June", value: "06" },
  { label: "July", value: "07" },
  { label: "August", value: "08" },
  { label: "September", value: "09" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
  { label: "January", value: "01" },
  { label: "February", value: "02" },
  { label: "March", value: "03" },
];

const getStartYearFromFY = (fy) => Number(fy?.split("-")[0]);

/* ======================================================
✅ COMMON REMOVE DESCRIPTION FILTER (GLOBAL)
====================================================== */
const filterRemovedDescriptions = (rows = []) => {
  const REMOVE_DESCRIPTIONS = new Set([
    "Value Added",
    "Value Added as a % of Revenue",
  ]);

  return (Array.isArray(rows) ? rows : []).filter(
    (row) => !REMOVE_DESCRIPTIONS.has(String(row?.DESCRIPTION || "").trim())
  );
};

/* ========= Utilities ========= */

const isPercentage = (val) =>
  val !== null && val !== undefined && val.toString().includes("%");

const getNumericValue = (val) =>
  Number((val || "0").toString().replace(/,/g, "").replace("%", ""));

const formatNumber = (val) =>
  val.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/* ========= Component ========= */

const OperatingPerformanceUnitWiseReport = () => {
  const dispatch = useDispatch();

  const {
    operatingPerformanceByUnit,
    operatingPersonalPaymentByUnit,
    operatingMiscExpensesByUnit,
    notesToOperatingByUnit,
    loading,
  } = useSelector((state) => state.unitReport);

  const [activeTab, setActiveTab] = useState("0");
  const [selectedFY, setSelectedFY] = useState(DEFAULT_FY);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedReportType, setSelectedReportType] = useState("For");

  const selectedFYStartYear = useMemo(
    () => getStartYearFromFY(selectedFY),
    [selectedFY]
  );

  /* ========= API CALL ========= */

  useEffect(() => {
  if (!selectedFYStartYear) return;

  let from_year, to_year, from_month, to_month;

  const monthNumber = Number(selectedMonth);

  // ✅ If report type is "upto"
  if (selectedReportType === "Upto") {
    from_year = selectedFYStartYear;
    from_month = "04";

    if (!selectedMonth) {
      to_year = selectedFYStartYear + 1;
      to_month = "03";
    } else if (monthNumber >= 4 && monthNumber <= 12) {
      to_year = selectedFYStartYear;
      to_month = selectedMonth;
    } else {
      to_year = selectedFYStartYear + 1;
      to_month = selectedMonth;
    }
  } 
  // ✅ Normal month-based logic
  else {
    if (!selectedMonth) {
      from_year = selectedFYStartYear;
      from_month = "04";
      to_year = selectedFYStartYear + 1;
      to_month = "03";
    } else {
      if (monthNumber >= 4 && monthNumber <= 12) {
        from_year = selectedFYStartYear;
        to_year = selectedFYStartYear;
      } else {
        from_year = selectedFYStartYear + 1;
        to_year = selectedFYStartYear + 1;
      }

      from_month = selectedMonth;
      to_month = selectedMonth;
    }
  }

  const payload = {
    from_year,
    from_month,
    to_year,
    to_month,
    report_type: selectedReportType,
    mnfUnit: "",
  };

  switch (activeTab) {
    case "0":
      dispatch(getOperatingPerformanceByUnit(payload));
      break;
    case "1":
      dispatch(getNotesToOperatingByUnit(payload));
      break;
    case "2":
      dispatch(getOperatingPersonalPaymentByUnit(payload));
      break;
    case "3":
      dispatch(getOperatingMiscExpensesByUnit(payload));
      break;
    default:
      break;
  }
}, [
  dispatch,
  activeTab,
  selectedFYStartYear,
  selectedReportType,
  selectedMonth,
]);

  /* ========= DATA SWITCH ========= */

  const data = useMemo(() => {
    let selectedData = [];

    switch (activeTab) {
      case "0":
        selectedData = operatingPerformanceByUnit || [];
        break;
      case "1":
        selectedData = notesToOperatingByUnit || [];
        break;
      case "2":
        selectedData = operatingPersonalPaymentByUnit || [];
        break;
      case "3":
        selectedData = operatingMiscExpensesByUnit || [];
        break;
      default:
        selectedData = [];
    }

    // ✅ COMMON FILTER APPLIED HERE
    return filterRemovedDescriptions(selectedData);
  }, [
    activeTab,
    operatingPerformanceByUnit,
    notesToOperatingByUnit,
    operatingPersonalPaymentByUnit,
    operatingMiscExpensesByUnit,
  ]);

  /* ========= Month Keys ========= */

  const monthKeys = useMemo(() => {
    if (!data?.length) return [];

    const ignore = new Set(["HEAD_ID", "DESCRIPTION", "isHeading", "key", "ACTUAL_LAST_YEAR"]);
    const sample = data.find((x) => String(x?.isHeading) !== "1") || data[0];

    return Object.keys(sample || {}).filter((k) => !ignore.has(k));
  }, [data]);

  /* ========= Columns ========= */

  const columns = useMemo(() => {
    if (!data.length) return [];

    const baseColumns = [
      {
        title: "Description",
        dataIndex: "DESCRIPTION",
        key: "DESCRIPTION",
        fixed: "left",
        width: 250,
      },

      ...monthKeys.map((key) => ({
        title: key.replace("_", "-"),
        dataIndex: key,
        key,
        align: "right",
        width: 130,
        render: (val, record) => {
          if (String(record?.isHeading) === "1") return "\u00A0";
          if (isPercentage(val)) return val;
          return formatNumber(getNumericValue(val));
        },
      })),
    ];

   

    return [
      ...baseColumns,
      {
        title: "Total",
        key: "row_total",
        align: "right",
        width: 160,
        fixed: "right",
        render: (_, record) => {
          if (String(record?.isHeading) === "1") return "\u00A0";

          const total = monthKeys.reduce((sum, key) => {
            const value = record[key];
            if (isPercentage(value)) return sum;
            return sum + getNumericValue(value);
          }, 0);

          return formatNumber(total);
        },
      },
    ];
  }, [data, monthKeys, selectedReportType]);

  /* ========= Table Data ========= */

  const tableData = useMemo(() => {
    if (!data?.length) return [];

    const KEEP_KEYS = new Set(["HEAD_ID", "DESCRIPTION", "isHeading", "key"]);

    return data.map((item, index) => {
      const isHeadingRow = String(item?.isHeading) === "1";

      if (!isHeadingRow) return { ...item, key: index };

      const row = { ...item, key: index };
      Object.keys(row).forEach((k) => {
        if (!KEEP_KEYS.has(k)) row[k] = "";
      });

      return row;
    });
  }, [data]);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="loader w-10 h-10 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-5">
      <div className="flex gap-3 items-center mb-4">
        <img src={ActiveProject} alt="region" />
        <div>
          <div className="text-[24px] font-semibold">
           Performance Plan - Operating Report
          </div>
          <div className="text-gray-500 text-sm">
            * All figures are in Rupees (in lakhs)
          </div>
        </div>
      </div>

      <CustomTab tabItems={tabData} activeTab={activeTab} setActiveTab={setActiveTab} />

      <div className="mt-6 bg-white shadow-lg rounded-xl p-4">
        <div className="flex justify-end gap-3 mb-4">
          <Select
            className="w-[12rem]"
            value={selectedFY}
            onChange={(val) => {
              setSelectedFY(val);
              setSelectedMonth(null);
            }}
            options={[
              { label: "FY 2023-24", value: "2023-2024" },
              { label: "FY 2024-25", value: "2024-2025" },
              { label: "FY 2025-26", value: "2025-2026" },
              { label: "FY 2026-27", value: "2026-2027" },
              { label: "FY 2027-28", value: "2027-2028" },
              { label: "FY 2028-29", value: "2028-2029" },
              { label: "FY 2029-30", value: "2029-2030" },
            ]}
          />

          <Select
            className="w-[12rem]"
            value={selectedMonth}
            onChange={setSelectedMonth}
            options={monthOptions}
            placeholder="Select Month"
            allowClear
          />

          <Select
            className="w-[12rem]"
            value={selectedReportType}
            onChange={setSelectedReportType}
            options={[
              { label: "For", value: "For" },
              { label: "Upto", value: "Upto" },
            ]}
          />
        </div>

        <ConsolidatedReportsTable
          columns={columns}
          dataSource={tableData}
          scroll={{ x: 1800 }}
          rowClassName={(record) =>
            record.key === "grand_total" ? "font-bold bg-gray-100" : ""
          }
        />
      </div>
    </div>
  );
};

export default OperatingPerformanceUnitWiseReport;